// Stock counts tab of a place (CR_Manager_vs_User_role P5; schedule and owners CR_20260914).
//
// Two tables used to show the same count twice — the top one as "7 still to check", the one below as "Open" — which
// read as two contradictory statuses for one thing (owner, 2026-09-18). Now the schedule is a schedule (when, who,
// next due, and the button to start) and there is **one** table of counts, newest first, with the count in progress
// as its top row. A count has exactly one status, in exactly one place.
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { type CountStatus, staleCount } from '@fleetspoke/core/counts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSession } from '../../auth/session';
import { Empty, Loading, LoadError } from '../Page';
import { useProfileNames } from '../../data/billing';
import { useLiveInvalidate } from '../../data/realtime';
import { type CountRow, useCounts, useCountStatus } from '../../data/workflow';
import { openCountText, ownersText, scheduleText } from '../../lib/counts';
import { isoDateIn } from '../../lib/billing';
import { calendarDate, dateTime } from '../../lib/time';
import { BUCKETS, NewCountDialog } from '../../pages/CountsPage';

/** When a count is due here, who may do it, and the button to start one. No result and no status: the table below
 *  owns those, and saying them twice is what made the tab contradict itself. */
function ScheduleTable({
  rows,
  several,
  onStart,
}: {
  rows: CountStatus[];
  several: boolean;
  onStart(siteId: string): void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  return (
    <Box sx={{ overflowX: 'auto', mb: 4 }}>
      <Table size="small" data-testid="count-sites">
        <TableHead>
          <TableRow>
            {several ? <TableCell>{t('counts.site')}</TableCell> : null}
            <TableCell>{t('counts.col_schedule')}</TableCell>
            <TableCell>{t('counts.col_owners')}</TableCell>
            <TableCell>{t('counts.col_next')}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((s) => (
            <TableRow key={s.site_id}>
              {several ? <TableCell>{s.site_name}</TableCell> : null}
              <TableCell>{scheduleText(s, t)}</TableCell>
              <TableCell sx={{ color: s.owners.length ? undefined : 'text.secondary' }}>
                {ownersText(s, t)}
              </TableCell>
              <TableCell>
                {calendarDate(s.next_due, i18n.language) ?? '—'}
                {s.previous_missed ? (
                  <Typography variant="caption" color="error" display="block">
                    {t('counts.missed', { date: calendarDate(s.previous_missed, i18n.language) })}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell align="right">
                {s.open_count ? (
                  <Button size="small" onClick={() => void navigate(`/counts/${s.open_count!.id}`)}>
                    {t('counts.open_here')}
                  </Button>
                ) : s.can_count ? (
                  <Button size="small" onClick={() => onStart(s.site_id)}>
                    {t('counts.start')}
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

/** Every count at this place, newest first — the one in progress is simply the top row. */
function CountsTable({
  rows,
  status,
  several,
}: {
  rows: CountRow[];
  status: CountStatus[];
  several: boolean;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { me } = useSession();
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const names = useProfileNames(rows.map((c) => c.closed_by).filter((x): x is string => !!x));
  // the live "N still to check" for the open count comes from count_status(), which the schedule rows already hold
  const openAt = new Map(status.filter((s) => s.open_count).map((s) => [s.site_id, s.open_count!]));
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" data-testid="count-history">
        <TableHead>
          <TableRow>
            {several ? <TableCell>{t('counts.site')}</TableCell> : null}
            <TableCell>{t('counts.started')}</TableCell>
            <TableCell>{t('counts.status')}</TableCell>
            <TableCell>{t('counts.col_closed')}</TableCell>
            <TableCell>{t('counts.col_by')}</TableCell>
            {BUCKETS.map((b) => (
              <TableCell key={b} align="right">
                {t(`counts.bucket_${b}`)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((c) => {
            const open = c.status === 'open';
            return (
              <TableRow
                key={c.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => void navigate(`/counts/${c.id}`)}
              >
                {several ? <TableCell>{c.sites?.name}</TableCell> : null}
                <TableCell>{dateTime(c.created_at, i18n.language, tz)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={open ? 'info' : 'default'}
                    variant={open ? 'filled' : 'outlined'}
                    label={
                      open
                        ? `${t('counts.status_in_progress')} · ${openCountText(openAt.get(c.site_id), t)}`
                        : t('counts.status_closed')
                    }
                  />
                </TableCell>
                <TableCell>{dateTime(c.closed_at, i18n.language, tz) ?? '—'}</TableCell>
                <TableCell>{(c.closed_by && names.data?.get(c.closed_by)) || '—'}</TableCell>
                {BUCKETS.map((b) => (
                  <TableCell key={b} align="right">
                    {c.summary[b] ?? 0}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

export function PlaceCounts({ siteIds }: { siteIds: string[] }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { me } = useSession();
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const status = useCountStatus();
  const counts = useCounts(siteIds);
  const [creating, setCreating] = useState<string | null>(null);
  useLiveInvalidate(
    `counts-${siteIds.join(',')}`,
    ['stock_counts', 'stock_count_lines'],
    [['counts']],
  );
  const rows = (status.data ?? []).filter((s) => siteIds.includes(s.site_id));
  const history = counts.data ?? [];
  const several = siteIds.length > 1;
  // counts left open past their day: they block every new count at that place, so say so here, where someone
  // wondering why "Start count" is missing will be looking (owner, 2026-09-18)
  const stale = rows
    .map((s) => ({
      s,
      open: s.open_count,
      age: s.open_count
        ? staleCount(isoDateIn(tz, new Date(s.open_count.created_at)), s.today)
        : null,
    }))
    .filter((x) => x.open && x.age?.stale);
  return (
    <Box data-testid="place-counts">
      {stale.map(({ s, open, age }) => (
        <Alert
          key={s.site_id}
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="stale-count"
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => void navigate(`/counts/${open!.id}`)}
            >
              {t('counts.go_close')}
            </Button>
          }
        >
          <AlertTitle>
            {several ? `${s.site_name} · ` : ''}
            {t('counts.stale_title', {
              date: dateTime(open!.created_at, i18n.language, tz),
              days: age!.days,
            })}
          </AlertTitle>
          {t('counts.stale_short', { date: dateTime(open!.created_at, i18n.language, tz) })}
        </Alert>
      ))}
      {status.isLoading ? <Loading /> : null}
      {status.error ? <LoadError error={status.error} /> : null}
      {status.data && !rows.length ? <Empty>{t('counts.state_not_scheduled')}</Empty> : null}
      {rows.length ? (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('counts.schedule_title')}
          </Typography>
          <ScheduleTable rows={rows} several={several} onStart={setCreating} />
        </>
      ) : null}

      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('counts.timeline_title')}
      </Typography>
      {counts.isLoading ? <Loading /> : null}
      {counts.error ? <LoadError error={counts.error} /> : null}
      {counts.data && !history.length ? <Empty>{t('counts.none')}</Empty> : null}
      {history.length ? <CountsTable rows={history} status={rows} several={several} /> : null}
      {creating !== null ? (
        <NewCountDialog siteId={creating || undefined} onClose={() => setCreating(null)} />
      ) : null}
    </Box>
  );
}
